/**
 * AI Vulnerability Discovery — the new paradigm, two halves.
 *
 * Section 1 "Vulnerabilities Found by AI" (industry trend): tracks the
 * shift to AI-discovered vulns (Anthropic Mythos, Google Big Sleep, XBOW,
 * OpenAI defender models, …) — surge curve, by-finder breakdown, and which
 * AI-found CVEs hit our own inventory.
 *
 * Section 2 "AI-Reasoned Findings" (our new spotter): the LLM-reasoning
 * vuln spotter that surfaces non-obvious chains the 15 hand-coded toxic
 * patterns miss. Each finding is a review-queue card — confirm/dismiss —
 * with the inference chain, validation steps, why rules miss it, and the
 * adversarial refuter verdict. Findings never auto-apply (pending_review).
 */
import { useCallback, useEffect, useState } from 'react'
import {
  Sparkles, RefreshCw, ExternalLink,
  Bot, ShieldAlert, ChevronDown, ChevronRight, Check, X, Play,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'
import MiniBarChart from '../components/MiniBarChart'
import RelatedItems from '../components/RelatedItems'
import KpiTile from '../components/KpiTile'
import SeverityBadge from '../components/SeverityBadge'
import PageHeader from '../components/PageHeader'
import ErrorBanner from '../components/ErrorBanner'
import EmptyState from '../components/EmptyState'

interface MonthBucket { month: string; count: number }
interface RecentDisc {
  title: string; finder_id: string; finder_name: string; kind: string
  confidence: number; cve_id: string | null; source: string
  url: string | null; discovered_at: string
}
interface DiscoveredResp {
  total_ai_discovered: number
  by_finder: Record<string, number>
  by_month: MonthBucket[]
  recent: RecentDisc[]
  human_vs_ai_ratio_estimate: number
  surge_vs_prior_month: number
}
interface AffectingItem {
  cve_id: string; finder_name: string; title: string
  source_url: string | null; urgency: string
}
interface SkepticVote { vote: string | null; reasoning?: string; ran?: boolean }
interface Refutation {
  likely_valid?: boolean; refutation?: string; refuter_ran?: boolean
  skeptics?: Record<string, SkepticVote>; votes_valid?: number; skeptics_ran?: number
}
interface Reasoned {
  id: number; title: string; reasoning: string; affected_assets: string[]
  confidence: number; severity: string; validation_steps: string | null
  why_rules_miss_it: string | null; status: string; model_used: string | null
  refutation: Refutation | null; created_at: string
  lens: string | null; grounding_score: number | null; grounded: boolean | null
  priority: number | null; raw_self_confidence: number | null
  exploitability: string | null
}
interface Summary {
  half_a: { total_persisted: number; distinct_finders: number; affecting_inventory: number; last_90d: number }
  half_b: {
    pending_review: number; confirmed: number; dismissed: number; refuted: number; total: number
    top_priority?: number; grounded_count?: number; by_lens?: Record<string, number>
  }
}

const LENS_LABEL: Record<string, string> = {
  identity: 'identity', network_path: 'network', supply_chain: 'supply-chain',
  data_flow: 'data-flow', timing: 'timing', trust_assumption: 'trust',
}

const EXPLOIT_BADGE: Record<string, string> = {
  high: 'bg-emerald/10 text-emerald border-emerald/30',   // high effort = harder = good
  medium: 'bg-amber/10 text-amber-light border-amber/30',
  low: 'bg-rose/10 text-rose-light border-rose/30',        // low effort = trivial = bad
}

export default function AIVulnDiscoveryPage() {
  const [disc, setDisc] = useState<DiscoveredResp | null>(null)
  const [affecting, setAffecting] = useState<AffectingItem[]>([])
  const [reasoned, setReasoned] = useState<Reasoned[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [scanMsg, setScanMsg] = useState('')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [showRefuted, setShowRefuted] = useState(false)

  // Active queue (pending/confirmed/dismissed) vs refuter-rejected (dimmed).
  // Active queue sorts by priority desc — grounded high-severity tier-1 first.
  const activeReasoned = reasoned
    .filter(f => f.status !== 'refuted')
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
  const refutedReasoned = reasoned.filter(f => f.status === 'refuted')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [d, a, r, s] = await Promise.all([
        api.get('/api/ai-vuln/discovered', { params: { window_days: 90 } }),
        api.get('/api/ai-vuln/discovered/affecting-us'),
        api.get('/api/ai-vuln/reasoned', { params: { limit: 100 } }),
        api.get('/api/ai-vuln/summary'),
      ])
      setDisc(d.data as DiscoveredResp)
      setAffecting((a.data.items || []) as AffectingItem[])
      setReasoned((r.data.items || []) as Reasoned[])
      setSummary(s.data as Summary)
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const runScan = async () => {
    setScanning(true); setScanMsg(''); setError('')
    try {
      const r = await api.post('/api/ai-vuln/reasoned/scan')
      const d = r.data as { proposed: number; queued: number; refuted: number; lenses_run?: number; grounded_count?: number; llm_calls?: number }
      setScanMsg(`Pass complete — ${d.lenses_run ?? 0} lenses, proposed ${d.proposed}, grounded ${d.grounded_count ?? 0}, queued ${d.queued}, refuted ${d.refuted} (${d.llm_calls ?? 0} LLM calls).`)
      await load()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      setError(status === 401 ? 'Admin role required to run a reasoning pass.' : 'Scan failed.')
    } finally {
      setScanning(false)
    }
  }

  const decide = async (id: number, action: 'confirm' | 'dismiss') => {
    try {
      await api.post(`/api/ai-vuln/reasoned/${id}/${action}`)
      await load()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      setError(status === 401 ? 'Admin role required.' : 'Action failed.')
    }
  }

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const finderEntries = disc ? Object.entries(disc.by_finder).sort((a, b) => b[1] - a[1]) : []
  const surgeVals = disc ? disc.by_month.map(m => m.count) : []
  const surgePts = disc ? disc.by_month.map(m => ({ t: m.month, v: m.count })) : []

  return (
    <div className="space-y-5 animate-fade-in">
      <PageHeader
        icon={Sparkles} iconClassName="text-cyan" title="AI VULNERABILITY DISCOVERY"
        subtitle="the new paradigm — vulns found by AI · LLM-reasoned non-obvious chains"
        backTo="/app/attack-paths" backLabel="Attack Paths"
        actions={
          <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />

      <ErrorBanner message={error} />

      {/* ─── Section 1 — Vulnerabilities Found by AI ─────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-sm font-bold text-text-primary flex items-center gap-2">
          <Bot size={15} className="text-amber" /> Vulnerabilities Found by AI
          <span className="text-[10px] font-normal text-text-muted">industry trend · last 90 days</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile label="AI-discovered (90d)" value={disc?.total_ai_discovered ?? '—'}
                hint={`ratio est. ${disc ? (disc.human_vs_ai_ratio_estimate * 100).toFixed(1) : '0'}% of corpus`} />
          <KpiTile label="Distinct AI finders" value={finderEntries.length || (summary?.half_a.distinct_finders ?? 0)} />
          <KpiTile label="Affecting our inventory" value={affecting.length}
                tone={affecting.length ? 'text-rose-light' : 'text-text-primary'}
                hint="AI-found = weaponised fast" />
          <KpiTile label="Surge vs prior month"
                value={disc ? `${disc.surge_vs_prior_month >= 0 ? '+' : ''}${disc.surge_vs_prior_month}` : '—'}
                tone={disc && disc.surge_vs_prior_month > 0 ? 'text-amber-light' : 'text-text-primary'} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted mb-2">Surge curve (by month)</div>
            {surgeVals.length > 1
              ? <Sparkline points={surgePts} width={300} height={48} tone="amber" showDot />
              : <div className="text-[11px] text-text-muted">Not enough data points yet.</div>}
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted mb-2">By AI finder</div>
            {finderEntries.length ? (
              <div className="space-y-1.5">
                <MiniBarChart values={finderEntries.map(e => e[1])}
                              labels={finderEntries.map(e => e[0])}
                              tone="amber" width={300} height={36} />
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                  {finderEntries.map(([name, n]) => (
                    <span key={name} className="text-[10px] text-text-secondary">
                      {name} <span className="font-mono text-amber-light">{n}</span>
                    </span>
                  ))}
                </div>
              </div>
            ) : <div className="text-[11px] text-text-muted">No AI-discovered vulns in window.</div>}
          </div>
        </div>

        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-2">
            Recent AI-discovered vulnerabilities
          </div>
          {disc && disc.recent.length ? (
            <div className="space-y-1">
              {disc.recent.map((r, i) => {
                const hitsUs = !!(r.cve_id && affecting.some(a => a.cve_id?.toUpperCase() === r.cve_id?.toUpperCase()))
                return (
                  <div key={i} className="flex items-center gap-2 border border-border rounded p-2 bg-surface-raised">
                    <span className="text-[9px] px-2 py-0.5 rounded border border-amber/30 bg-amber/10 text-amber-light whitespace-nowrap">
                      {r.finder_name}
                    </span>
                    <span className="text-[11px] text-text-primary flex-1 truncate">{r.title}</span>
                    {r.cve_id && <span className="text-[10px] font-mono text-text-muted">{r.cve_id}</span>}
                    {hitsUs && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded border border-rose/30 bg-rose/10 text-rose-light whitespace-nowrap inline-flex items-center gap-0.5">
                        <ShieldAlert size={10} /> affects us
                      </span>
                    )}
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-text-muted hover:text-cyan">
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          ) : <div className="text-[11px] text-text-muted">No recent AI-discovered vulnerabilities.</div>}
        </div>
      </section>

      {/* ─── Section 2 — AI-Reasoned Findings ────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-sm font-bold text-text-primary flex items-center gap-2">
            <Sparkles size={15} className="text-cyan" /> AI-Reasoned Findings
            <span className="text-[10px] font-normal text-text-muted">
              our LLM spotter · complements the 15 hand-coded patterns · review queue
            </span>
          </h2>
          <div className="ml-auto flex items-center gap-2">
            {scanMsg && <span className="text-[10px] text-emerald">{scanMsg}</span>}
            <button type="button" onClick={() => void runScan()} disabled={scanning} className="btn-ghost">
              {scanning ? <RefreshCw size={11} className="animate-spin" /> : <Play size={11} />}
              {scanning ? 'Reasoning…' : 'Run reasoning pass'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile label="Pending review" value={summary?.half_b.pending_review ?? 0}
                tone={(summary?.half_b.pending_review ?? 0) ? 'text-cyan' : 'text-text-primary'}
                hint={summary?.half_b.top_priority != null ? `top priority ${summary.half_b.top_priority.toFixed(0)}` : undefined} />
          <KpiTile label="Confirmed" value={summary?.half_b.confirmed ?? 0} tone="text-emerald" />
          <KpiTile label="Grounded (real inventory)" value={summary?.half_b.grounded_count ?? 0}
                tone={(summary?.half_b.grounded_count ?? 0) ? 'text-cyan' : 'text-text-muted'}
                hint="matched to real assets" />
          <KpiTile label="Refuter-rejected" value={summary?.half_b.refuted ?? 0} tone="text-rose-light" />
        </div>

        {summary?.half_b.by_lens && Object.keys(summary.half_b.by_lens).length > 0 && (
          <div className="card p-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-[10px] uppercase tracking-widest text-text-muted">By reasoning lens</span>
            {Object.entries(summary.half_b.by_lens).sort((a, b) => b[1] - a[1]).map(([lens, n]) => (
              <span key={lens} className="text-[10px] text-text-secondary">
                {LENS_LABEL[lens] || lens} <span className="font-mono text-cyan">{n}</span>
              </span>
            ))}
          </div>
        )}

        {activeReasoned.length === 0 ? (
          <EmptyState variant="inline" icon={Sparkles} tone="text-cyan"
            title="No active reasoned findings"
            hint={<>Run a pass or wait for the daily scan. Findings land here as
              pending review — they are never auto-applied.
              {refutedReasoned.length > 0 && ` ${refutedReasoned.length} finding(s) were proposed but rejected by the adversarial refuter — see below.`}</>} />
        ) : (
          <div className="space-y-2">
            {activeReasoned.map(f => renderFinding(f, expanded.has(f.id), toggle, decide, false))}
          </div>
        )}

        {/* Refuter-rejected findings — dimmed, still visible so the analyst can
            judge the refuter's calibration and catch a wrongly-killed finding. */}
        {refutedReasoned.length > 0 && (
          <div className="space-y-2">
            <button type="button" onClick={() => setShowRefuted(v => !v)}
                    className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-text-muted hover:text-text-secondary pt-2">
              {showRefuted ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <ShieldAlert size={12} className="text-rose-light/70" />
              Proposed but rejected by refuter ({refutedReasoned.length})
            </button>
            {showRefuted && (
              <div className="space-y-2 opacity-60">
                {refutedReasoned.map(f => renderFinding(f, expanded.has(f.id), toggle, decide, true))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  )
}

function renderFinding(
  f: Reasoned,
  open: boolean,
  toggle: (id: number) => void,
  decide: (id: number, action: 'confirm' | 'dismiss') => void,
  isRefuted: boolean,
) {
  return (
    <div key={f.id} className={clsx('card p-0 overflow-hidden', isRefuted && 'border-rose/15')}>
      <button type="button" onClick={() => toggle(f.id)}
              className="w-full flex items-center gap-2 p-3 text-left hover:bg-surface-raised">
        {open ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
        {f.priority != null && (
          <span className={clsx('text-[10px] font-mono px-1.5 py-0.5 rounded border w-12 text-center',
            f.priority >= 50 ? 'bg-rose/10 text-rose-light border-rose/30'
              : f.priority >= 25 ? 'bg-amber/10 text-amber-light border-amber/30'
              : 'bg-surface-raised text-text-muted border-border')}
            title="priority (severity × confidence × grounding × asset criticality)">
            {Math.round(f.priority)}
          </span>
        )}
        <SeverityBadge severity={f.severity} width="w-16" />
        {f.lens && (
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-cyan/30 bg-cyan/10 text-cyan whitespace-nowrap">
            {LENS_LABEL[f.lens] || f.lens}
          </span>
        )}
        {f.grounding_score != null && (
          <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap',
            f.grounded ? 'border-cyan/30 text-cyan' : 'border-amber/30 text-amber-light')}
            title={f.grounded ? 'grounded — claims match real inventory' : 'ungrounded — speculative / unmatched claims'}>
            {f.grounded ? '🔗' : '⚠️'} {Math.round((f.grounding_score ?? 0) * 100)}%
          </span>
        )}
        {f.exploitability && (
          <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap',
            EXPLOIT_BADGE[f.exploitability] || 'border-border text-text-muted')}
            title="attacker effort to exploit">
            {f.exploitability} effort
          </span>
        )}
        <span className={clsx('text-[12px] font-medium flex-1', isRefuted ? 'text-text-secondary line-through/0' : 'text-text-primary')}>{f.title}</span>
        {isRefuted && (
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-rose/30 text-rose-light">refuted</span>
        )}
        {!isRefuted && f.status !== 'pending_review' && (
          <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border',
            f.status === 'confirmed' ? 'border-emerald/30 text-emerald' : 'border-border text-text-muted')}>
            {f.status}
          </span>
        )}
        <div className="w-20 flex items-center gap-1">
          <div className="h-1.5 flex-1 rounded bg-surface-raised overflow-hidden">
            <div className={clsx('h-full', isRefuted ? 'bg-rose-light/50' : 'bg-cyan')} style={{ width: `${Math.round(f.confidence * 100)}%` }} />
          </div>
          <span className="text-[9px] font-mono text-text-muted">{Math.round(f.confidence * 100)}%</span>
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-border">
          <Field label="Reasoning (inference chain)" value={f.reasoning} />
          {f.affected_assets.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Affected assets</div>
              <div className="flex flex-wrap gap-1">
                {f.affected_assets.map((a, i) => (
                  <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-raised border border-border text-text-secondary">{a}</span>
                ))}
              </div>
            </div>
          )}
          {f.validation_steps && <Field label="Validation steps" value={f.validation_steps} />}
          {f.why_rules_miss_it && <Field label="Why rules miss it" value={f.why_rules_miss_it} />}
          {f.refutation && (
            <div className={clsx('rounded border p-2', isRefuted ? 'border-rose/25 bg-rose/5' : 'border-border bg-surface-raised')}>
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1 flex items-center gap-1 flex-wrap">
                <ShieldAlert size={11} /> Multi-skeptic verdict
                {f.refutation.refuter_ran && f.refutation.votes_valid != null && (
                  <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] border border-border text-text-secondary font-mono">
                    {f.refutation.votes_valid}/{f.refutation.skeptics_ran ?? 3} valid
                  </span>
                )}
                <span className={clsx('px-1.5 py-0.5 rounded text-[9px] border',
                  !f.refutation.refuter_ran ? 'border-border text-text-muted'
                    : f.refutation.likely_valid ? 'border-emerald/30 text-emerald' : 'border-rose/30 text-rose-light')}>
                  {!f.refutation.refuter_ran ? 'not run' : f.refutation.likely_valid ? 'survived' : 'rejected'}
                </span>
              </div>
              {f.refutation.skeptics && (
                <div className="flex flex-col gap-1 mb-1.5">
                  {Object.entries(f.refutation.skeptics).map(([sid, v]) => (
                    <div key={sid} className="flex items-start gap-2 text-[10px]">
                      <span className={clsx('px-1.5 py-0.5 rounded border w-14 text-center flex-shrink-0',
                        !v.ran ? 'border-border text-text-muted'
                          : v.vote === 'valid' ? 'border-emerald/30 text-emerald' : 'border-rose/30 text-rose-light')}>
                        {!v.ran ? '—' : v.vote}
                      </span>
                      <span className="text-text-muted font-mono w-28 flex-shrink-0">{sid}</span>
                      <span className="text-text-secondary">{v.reasoning}</span>
                    </div>
                  ))}
                </div>
              )}
              {!f.refutation.skeptics && f.refutation.refutation && (
                <div className="text-[11px] text-text-secondary whitespace-pre-wrap">{f.refutation.refutation}</div>
              )}
              {isRefuted && (
                <div className="text-[10px] text-text-muted mt-1.5 italic">
                  Kept for review — if you believe the refuter is wrong, confirm to move it into the active queue.
                </div>
              )}
            </div>
          )}
          <div className="text-[10px] text-text-muted flex flex-wrap gap-x-3">
            {f.model_used && <span>model: {f.model_used}</span>}
            {f.raw_self_confidence != null && (
              <span title="LLM self-reported vs calibrated (grounding + skeptic votes)">
                confidence: {Math.round(f.confidence * 100)}% calibrated
                {' '}(self-reported {Math.round(f.raw_self_confidence * 100)}%)
              </span>
            )}
          </div>
          {(f.status === 'pending_review' || isRefuted) && (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => decide(f.id, 'confirm')}
                      className="btn-ghost text-emerald">
                <Check size={12} /> {isRefuted ? 'Override — confirm anyway' : 'Confirm'}
              </button>
              <button type="button" onClick={() => decide(f.id, 'dismiss')}
                      className="btn-ghost text-text-muted">
                <X size={12} /> Dismiss
              </button>
            </div>
          )}
          <RelatedItems entityType="reasoned_finding" entityKey={String(f.id)} />
        </div>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">{label}</div>
      <div className="text-[12px] text-text-secondary whitespace-pre-wrap leading-relaxed">{value}</div>
    </div>
  )
}
